import { Button, Checkbox, clx, Input, Label, Select, Text, toast, usePrompt } from "@medusajs/ui"
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { useMemo, useState } from "react"
import { money } from "../lib/format"
import { t, tc, ts } from "../lib/i18n"
import { errorMessage, hqApi, type OrderDetail } from "../lib/medusa"
import { exchangeChoices, productOfVariant } from "../lib/returns"
import { newRequestId } from "../lib/stock-changes"

type Line = OrderDetail["items"][number] & { extras: OrderDetail["items"] }
type Mode = "change" | "remove"

const round2 = (n: number) => Math.round(n * 100) / 100
const sizeName = (option: string) => (option && !/^default/i.test(option) ? option : "")

/**
 * A piece of a site order that cannot be sent as it is, before the order leaves (owners, 9 Oct 2026):
 * CHANGED into another size or product in stock, or taken out. Both are Medusa's own order edit: the
 * old unit goes back on sale, a new one is reserved, the total follows, and what was paid above the new
 * total can be refunded in the same step. A dearer change is owed by a customer who already paid
 * (settled in a DM); a cash order simply collects the new total at hand-over.
 */
export function RemoveItem({ order, item, onClose }: { order: OrderDetail; item: Line; onClose: () => void }) {
  const queryClient = useQueryClient()
  const prompt = usePrompt()
  const [requestId] = useState(newRequestId)
  const [mode, setMode] = useState<Mode>("change")
  const open = item.quantity - item.fulfilled
  const [units, setUnits] = useState("1")
  const paid = order.payment.refundable > 0
  const card = order.payment.kind === "card"
  const [refund, setRefund] = useState(true)
  const [notify, setNotify] = useState(true)

  // The replacement: the same product first, any product of the shop with a size in stock.
  const profit = useQuery({ queryKey: ["hq", "product-profit"], queryFn: () => hqApi.productProfit(), enabled: mode === "change" })
  const choices = useMemo(() => exchangeChoices(profit.data), [profit.data])
  const [productId, setProductId] = useState<string | null>(null)
  const product = choices.find((c) => c.id === (productId ?? productOfVariant(profit.data, item.variant_id)?.id)) ?? null
  const [variantId, setVariantId] = useState<string | null>(null)
  const size = product?.sizes.find((s) => s.variant_id === variantId) ?? null
  const [price, setPrice] = useState<"new" | "same">("same")

  const count = Number(units)
  const valid = Number.isInteger(count) && count >= 1 && count <= open
  const unitPaid = item.paid_unit_price ?? item.unit_price
  const extrasPerUnit = item.extras.reduce((sum, extra) => sum + (extra.quantity > 0 ? extra.unit_price : 0), 0)
  const newUnit = price === "same" || product?.price == null ? item.unit_price : product.price
  // Removing gives back what was paid; a change is priced against the list price, because a discount
  // code on the order is applied again by Medusa to the new item.
  const delta = !valid || (mode === "change" && !size) ? 0 : mode === "remove" ? -round2(count * (unitPaid + extrasPerUnit)) : round2(count * (newUnit - item.unit_price))
  const newTotal = round2(order.total + delta)
  const back = paid && delta < 0 ? Math.min(order.payment.refundable, -delta) : 0
  const owes = paid && delta > 0 ? delta : 0
  const ready = valid && (mode === "remove" || (!!size && (size.available ?? 0) >= count))
  const name = [item.title, sizeName(item.variant ?? "")].filter(Boolean).join(" · ")
  const toName = product && size ? [product.title, sizeName(size.title)].filter(Boolean).join(" · ") : ""

  const save = useMutation({
    mutationFn: () =>
      hqApi.removeOrderItem(order.id, {
        line_item_id: item.id,
        units: count,
        request_id: requestId,
        refund: back > 0 && refund,
        notify,
        swap: mode === "change" && size ? { variant_id: size.variant_id, price } : null,
      }),
    onSuccess: (body) => {
      queryClient.setQueryData(["hq", "order", order.id], body.order)
      void queryClient.invalidateQueries({ queryKey: ["hq", "orders"] })
      void queryClient.invalidateQueries({ queryKey: ["hq", "product-profit"] })
      if (body.replay) toast.success(t("Already done."))
      else if (body.refund_error) toast.warning(t("Saved, but the refund failed: {error} Refund it with Refund at the top.", { error: ts(body.refund_error) }))
      else
        toast.success(
          [
            mode === "change" ? t("Changed. The old item is back on sale.") : t("Taken out. It is back on sale."),
            body.refunded > 0 ? (card ? t("{amount} refunded to the card.", { amount: money(body.refunded) }) : t("{amount} refunded (recorded).", { amount: money(body.refunded) })) : null,
            body.owed > 0 ? t("The customer owes {amount} more.", { amount: money(body.owed) }) : null,
          ]
            .filter(Boolean)
            .join(" ")
        )
      onClose()
    },
    onError: (error) => toast.error(errorMessage(error)),
  })

  async function confirm() {
    if (!ready) return
    const moneyLine =
      back > 0 && refund
        ? card
          ? t("{amount} goes back to the customer's card through Stripe. This is real money and cannot be undone.", { amount: money(back) })
          : t("The refund is recorded. Give the {amount} back in cash yourself.", { amount: money(back) })
        : owes > 0
          ? t("The customer owes about {amount} more: settle it in a DM (cash at hand-over, or a payment link).", { amount: money(owes) })
          : null
    const ok = await prompt({
      title: mode === "change" ? t("Change {n} × {name} into {to}?", { n: count, name, to: toName }) : t("Take {n} × {name} out of {ref}?", { n: count, name, ref: order.ref }),
      description: [
        mode === "change"
          ? t("{name} goes back on sale, {to} is reserved for this order, and the total becomes about {amount}.", { name, to: toName, amount: money(newTotal) })
          : t("It goes back on sale and the order's total drops by about {amount}.", { amount: money(-delta) }),
        moneyLine,
      ]
        .filter(Boolean)
        .join(" "),
      confirmText: mode === "change" ? t("Change the item") : t("Remove from the order"),
      cancelText: t("Back"),
      variant: mode === "change" ? "confirmation" : "danger",
    })
    if (ok) save.mutate()
  }

  const pill = (active: boolean) =>
    clx(
      "txt-compact-xsmall-plus h-8 rounded-full border px-3 transition-colors",
      active ? "bg-ui-fg-base text-ui-bg-base border-transparent" : "bg-ui-bg-base text-ui-fg-subtle border-ui-border-base"
    )

  return (
    <div className="bg-ui-bg-subtle flex w-full flex-col gap-y-2.5 rounded-lg p-3">
      <div role="group" aria-label={t("Change or remove")} className="flex flex-wrap gap-1.5">
        <button type="button" aria-pressed={mode === "change"} className={pill(mode === "change")} onClick={() => setMode("change")}>{t("Change it for another")}</button>
        <button type="button" aria-pressed={mode === "remove"} className={pill(mode === "remove")} onClick={() => setMode("remove")}>{t("Remove it")}</button>
      </div>

      {open > 1 && (
        <div className="flex items-center gap-x-2">
          <Label htmlFor={`units-${item.id}`} className="txt-compact-small">{t("How many")}</Label>
          <div className="w-16">
            <Input id={`units-${item.id}`} size="small" inputMode="numeric" value={units} onChange={(event) => setUnits(event.target.value)} aria-invalid={!valid} />
          </div>
          <Text size="xsmall" className="text-ui-fg-muted">{t("of {n}", { n: open })}</Text>
        </div>
      )}

      {mode === "change" && (
        <div className="flex flex-col gap-y-2">
          {profit.isLoading && <Text size="xsmall" className="text-ui-fg-muted">{t("Loading…")}</Text>}
          {profit.data && (
            <div className="grid gap-2 sm:grid-cols-2">
              <div className="flex flex-col gap-y-1">
                <Label size="xsmall" weight="plus">{t("Product")}</Label>
                <Select
                  value={product?.id}
                  onValueChange={(value) => {
                    setProductId(value)
                    setVariantId(null)
                  }}
                >
                  <Select.Trigger><Select.Value placeholder={t("Choose a product")} /></Select.Trigger>
                  <Select.Content>
                    {choices.map((choice) => <Select.Item key={choice.id} value={choice.id}>{choice.title}</Select.Item>)}
                  </Select.Content>
                </Select>
              </div>
              <div className="flex flex-col gap-y-1">
                <Label size="xsmall" weight="plus">{t("Size")}</Label>
                <Select value={variantId ?? undefined} onValueChange={setVariantId} disabled={!product}>
                  <Select.Trigger><Select.Value placeholder={t("Choose a size")} /></Select.Trigger>
                  <Select.Content>
                    {(product?.sizes ?? []).map((s) => (
                      <Select.Item key={s.variant_id} value={s.variant_id} disabled={s.variant_id === item.variant_id || (s.available ?? 0) < 1}>
                        {`${sizeName(s.title) || t("One size")} · ${s.variant_id === item.variant_id ? t("this one") : tc("stocked", "{n} available", { n: s.available ?? 0 })}`}
                      </Select.Item>
                    ))}
                  </Select.Content>
                </Select>
              </div>
            </div>
          )}
          {size && valid && (size.available ?? 0) < count && <Text size="xsmall" className="text-ui-fg-error">{t("Not enough of that size in stock.")}</Text>}
          {product && product.price != null && product.price !== item.unit_price && (
            <div className="flex flex-col gap-y-1">
              <Text size="xsmall" weight="plus">{t("Price")}</Text>
              <div role="group" aria-label={t("Price")} className="flex flex-wrap gap-1.5">
                <button type="button" aria-pressed={price === "same"} className={pill(price === "same")} onClick={() => setPrice("same")}>
                  {t("Same as paid ({amount})", { amount: money(item.unit_price) })}
                </button>
                <button type="button" aria-pressed={price === "new"} className={pill(price === "new")} onClick={() => setPrice("new")}>
                  {t("The new item's price ({amount})", { amount: money(product.price) })}
                </button>
              </div>
            </div>
          )}
          {item.extras.length > 0 && <Text size="xsmall" className="text-ui-fg-muted">{t("Its packaging stays with the new item when that product offers it; otherwise it comes out too.")}</Text>}
        </div>
      )}

      {(mode === "remove" || size) && (
        <Text size="xsmall" className="text-ui-fg-subtle">
          {t("New total about {amount}.", { amount: money(newTotal) })}
          {mode === "remove" && item.extras.length > 0 ? ` ${t("Its packaging comes out with it.")}` : ""}
        </Text>
      )}
      {back > 0 && (
        <div className="flex items-start gap-x-2">
          <Checkbox id={`refund-${item.id}`} checked={refund} onCheckedChange={(value) => setRefund(value === true)} className="mt-0.5" />
          <Label htmlFor={`refund-${item.id}`} className="txt-compact-small">
            {card ? t("Refund the difference to the card ({amount})", { amount: money(back) }) : t("Record the difference as refunded ({amount}), and give it back in cash", { amount: money(back) })}
          </Label>
        </div>
      )}
      {back > 0 && refund && (
        <div className="flex items-start gap-x-2">
          <Checkbox id={`notify-${item.id}`} checked={notify} onCheckedChange={(value) => setNotify(value === true)} className="mt-0.5" />
          <Label htmlFor={`notify-${item.id}`} className="txt-compact-small">{t("Email the customer about the refund")}</Label>
        </div>
      )}
      {owes > 0 && <Text size="xsmall" className="text-ui-fg-subtle">{t("The customer owes about {amount} more: settle it in a DM (cash at hand-over, or a payment link).", { amount: money(owes) })}</Text>}
      {!paid && (mode === "remove" || size) && <Text size="xsmall" className="text-ui-fg-muted">{t("Nothing has been paid yet: the customer pays the new total at hand-over.")}</Text>}

      <div className="flex flex-wrap gap-2">
        <Button size="small" variant={mode === "change" ? "primary" : "danger"} onClick={() => void confirm()} isLoading={save.isPending} disabled={!ready}>
          {mode === "change" ? t("Change the item") : t("Remove from the order")}
        </Button>
        <Button size="small" variant="secondary" onClick={onClose} disabled={save.isPending}>{t("Cancel")}</Button>
      </div>
    </div>
  )
}
